import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Youtube, FileText, ExternalLink, Search } from 'lucide-react';
import { API } from '../lib/apiBase';

const Reviews = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | youtube | article
  const [search, setSearch] = useState('');

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/review-links`);
      setLinks(data);
    } catch (error) {
      console.error('Error loading review links:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const filtered = links.filter((link) => {
    if (filter !== 'all' && link.type !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        link.title.toLowerCase().includes(s) ||
        link.author.toLowerCase().includes(s) ||
        (link.brand || '').toLowerCase().includes(s) ||
        (link.model || '').toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">
            ARAÇ İNCELEMELERİ
          </p>
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tighter mb-3" data-testid="reviews-title">
            Uzmanlardan İncelemeler
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            Türkiye'nin önde gelen otomotiv YouTuber'larından video incelemeler ve yazarların
            detaylı makaleleri tek bir yerde.
          </p>
        </div>

        {/* Filters */}
        <div className="border border-border bg-card p-4 mb-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex gap-2" data-testid="type-filters">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              className="rounded-sm"
              data-testid="filter-all"
            >
              Tümü
            </Button>
            <Button
              onClick={() => setFilter('youtube')}
              variant={filter === 'youtube' ? 'default' : 'outline'}
              size="sm"
              className="rounded-sm"
              data-testid="filter-youtube"
            >
              <Youtube className="w-4 h-4 mr-2" />
              YouTube
            </Button>
            <Button
              onClick={() => setFilter('article')}
              variant={filter === 'article' ? 'default' : 'outline'}
              size="sm"
              className="rounded-sm"
              data-testid="filter-article"
            >
              <FileText className="w-4 h-4 mr-2" />
              Makaleler
            </Button>
          </div>

          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Marka, model veya yazar ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-sm pl-9"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-border">
            <p className="text-muted-foreground">Henüz inceleme eklenmemiş veya filtreye uygun sonuç yok.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="review-links-grid">
            {filtered.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-border bg-card overflow-hidden group hover:border-primary transition-colors flex flex-col"
                data-testid={`review-card-${link.id}`}
              >
                {/* Thumbnail */}
                <div className="relative h-44 bg-secondary overflow-hidden">
                  {link.thumbnail_url ? (
                    <img
                      src={link.thumbnail_url}
                      alt={link.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {link.type === 'youtube' ? (
                        <Youtube className="w-12 h-12 text-muted-foreground" />
                      ) : (
                        <FileText className="w-12 h-12 text-muted-foreground" />
                      )}
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold uppercase tracking-wide ${
                        link.type === 'youtube'
                          ? 'bg-destructive text-white'
                          : 'bg-foreground text-background'
                      }`}
                    >
                      {link.type === 'youtube' ? <Youtube className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                      {link.type === 'youtube' ? 'Video' : 'Makale'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex-1 flex flex-col">
                  {(link.brand || link.model) && (
                    <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-2 font-mono">
                      {link.brand} {link.model}
                    </p>
                  )}

                  <h3 className="text-lg font-heading font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">
                    {link.title}
                  </h3>

                  {link.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                      {link.description}
                    </p>
                  )}

                  <div className="mt-auto flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">{link.author}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                      İncele
                      <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
